import {ApplicationConfig, inject, LOCALE_ID, provideAppInitializer, provideBrowserGlobalErrorListeners} from '@angular/core';
import {registerLocaleData} from '@angular/common';
import localeTh from '@angular/common/locales/th';
import {Router, provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig} from '@angular/router';
import {provideHttpClient, withInterceptors} from '@angular/common/http';

import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbModal,
  NgbTimeAdapter,
  NgbTimepickerI18n
} from '@ng-bootstrap/ng-bootstrap';

import {routes} from './app.routes';
import {
  AbstractSecurityStorage,
  authInterceptor,
  DefaultSessionStorageService,
  OidcSecurityService,
  provideAuth
} from 'angular-auth-oidc-client';
import {ThNgbDateParserFormatter} from './shared/datepicker/th-ngb-date-parser-formatter';
import {loadingInterceptor} from './shared/commons/interceptors/loading-interceptor';
import {http400Interceptor} from './shared/commons/interceptors/http400-interceptor';
import {http401Interceptor} from './shared/commons/interceptors/http401-interceptor';
import {http403Interceptor} from './shared/commons/interceptors/http403-interceptor';
import {http409Interceptor} from './shared/commons/interceptors/http409-interceptor';
import {http413Interceptor} from './shared/commons/interceptors/http413-interceptor';
import {http422Interceptor} from './shared/commons/interceptors/http422-interceptor';
import {http5xxInterceptor} from './shared/commons/interceptors/http5xx-interceptor';
import {CustomDatepickerI18n, I18n} from './shared/commons/gt-ng-datepicker/custom-datepicker';
import {CustomNgbDateNativeAdapterToString} from './shared/commons/gt-ng-datepicker/date-native-adaptor';
import {CustomNgbDateParser} from './shared/commons/gt-ng-datepicker/custom-ngb-date-parser';
import {CustomNgbTimeAdapter} from './shared/commons/gt-ng-datepicker/custom-time-native-adaptor';
import {CustomTimepickerI18n} from './shared/commons/gt-ng-datepicker/custom-timepicker';
import {authConfig} from './auth.config';
import {SafeNgbModal} from './shared/commons/safe-ngb-modal';
import {of} from 'rxjs';
import {SingleLogoutService} from './shared/auth/single-logout.service';
import {restoreSession} from './shared/auth/restore-session';
import {recoverFailedCallback} from './shared/auth/callback-recovery';

registerLocaleData(localeTh);

// Public routes that must NOT contact IAM on app bootstrap.
// Matches regardless of base href (e.g. /ui/pr-headless or /ui/floating-chat-embed).
function isPublicPath(pathname: string): boolean {
  return /\/(pr|pr-headless|public|qmobile|floating-chat-embed|error|unauthorized|logout)(\/|$)/.test(pathname);
}

export const appConfig: ApplicationConfig = {
  providers: [
    {provide: LOCALE_ID, useValue: 'th-TH'},
    {provide: NgbModal, useClass: SafeNgbModal},
    {provide: NgbDateParserFormatter, useClass: ThNgbDateParserFormatter},
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withRouterConfig({onSameUrlNavigation: "reload"}),
      withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
      withComponentInputBinding(),
    ),
    provideAuth(authConfig),
    // Replaces withAppInitializerAuthCheck(): skip checkAuth() on public routes
    // so /pr, /pr-headless, /public and /floating-chat-embed never trigger IAM (well-known/session/silent-renew).
    provideAppInitializer(() => {
      if (isPublicPath(window.location.pathname)) {
        return undefined;
      }
      const oidc = inject(OidcSecurityService);
      const router = inject(Router);
      inject(SingleLogoutService).start();
      const isCallback = /\/callback\/?$/.test(window.location.pathname);
      const session$ = restoreSession(oidc, {
        isCallback,
        onFailure: result => {
          oidc.logoffLocal();
          void router.navigateByUrl('/error/401');
          return of({...result, isAuthenticated: false, accessToken: '', idToken: '', userData: null});
        },
      });
      return isCallback ? session$.pipe(recoverFailedCallback(oidc, router)) : session$;
    }),
    provideHttpClient(
      // withFetch(),
      withInterceptors([
        authInterceptor(),
        loadingInterceptor,
        http400Interceptor,
        http401Interceptor,
        http403Interceptor,
        http409Interceptor,
        http413Interceptor,
        http422Interceptor,
        http5xxInterceptor
      ])
    ),
    I18n,
    {provide: AbstractSecurityStorage, useClass: DefaultSessionStorageService},
    {provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n},
    {provide: NgbDateAdapter, useClass: CustomNgbDateNativeAdapterToString},
    {provide: NgbDateParserFormatter, useClass: CustomNgbDateParser},
    {provide: NgbTimeAdapter, useClass: CustomNgbTimeAdapter},
    {provide: NgbTimepickerI18n, useClass: CustomTimepickerI18n},
  ]
};
